import React, { useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import Accessibility from 'highcharts/modules/accessibility';
import styled from 'styled-components';
import { chartConfigUtilService } from './chartConfigUtilService';

// Initialize accessibility module
Accessibility(Highcharts);

const ChartContainer = styled.div`
    width: 100%;
    height: 100%;

    // > div {
    //     height: 100%;
    // }

    .f-legend-dash {
        display: inline-block;
        vertical-align: middle;
        margin-right: 6px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
    }
`;

export interface GenericChartProps {
    /** Highcharts configuration options */
    config: Highcharts.Options;
    /** Enable rounded stacks for stacked column/bar charts */
    enableRoundedStacks?: boolean;
    /** Callback fired when chart is loaded/ready */
    onChartLoaded?: (chart: Highcharts.Chart) => void;
}

export const GenericChart: React.FC<GenericChartProps> = ({
    config,
    enableRoundedStacks = false,
    onChartLoaded,
}: GenericChartProps) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<Highcharts.Chart | null>(null);
    const onChartLoadedRef = useRef(onChartLoaded);

    // Keep callback ref up to date
    useEffect(() => {
        onChartLoadedRef.current = onChartLoaded;
    }, [onChartLoaded]);

    // Enable rounded stacks before chart creation if requested
    useEffect(() => {
        if (enableRoundedStacks) {
            chartConfigUtilService.enableRoundedStacks();
        }
    }, [enableRoundedStacks]);

    // Recreate the chart whenever `config` changes. A full create runs Highcharts'
    // complete layout pass -- including the pie data-label overflow handling that
    // useHTML labels rely on to stay inside the plot. (chart.update() skips that for
    // useHTML labels, which left labels clipped on data changes.)
    //
    // Callers keep `config` referentially stable (memoized config + previousData while
    // loading), so this effect fires once per real data change rather than per render,
    // which keeps the recreate from flashing on every parent re-render.
    useEffect(() => {
        if (!chartRef.current) {
            return undefined;
        }

        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
            chartInstanceRef.current = null;
        }

        try {
            chartInstanceRef.current = Highcharts.chart(chartRef.current, config);
            onChartLoadedRef.current?.(chartInstanceRef.current);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error rendering Highcharts chart:', error, config);
        }

        return () => {
            chartInstanceRef.current?.destroy();
            chartInstanceRef.current = null;
        };
    }, [config]);

    return <ChartContainer ref={chartRef} className="c-highcharts-generic" />;
};

export default GenericChart;
